import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  LogOut,
  MapPin,
  Users,
  Wallet,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { env } from "@/lib/env";
import { formatMoneyMinor } from "@/lib/money";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/brand/PageLoader";
import { DownloadAppCTA } from "@/components/brand/DownloadAppCTA";

interface Match {
  id: string;
  status: string;
  target_players: number;
  unit_price_minor: number;
  platform_fee_minor: number;
  currency: string;
  format: string;
  slot: {
    starts_at: string;
    ends_at: string;
  } | null;
  court: {
    id: string;
    name: string;
    venue: {
      venue_name: string;
      city: string;
      address_line1: string;
    } | null;
  } | null;
  participants: Participant[] | null;
}

interface Participant {
  id: string;
  user_id: string;
  status: string;
  joined_at: string;
  profile: {
    display_name: string | null;
  } | null;
}

/**
 * Public match detail page. Anyone can see the roster; only logged-in
 * users can join or leave. Payments are handled via Stripe Checkout
 * (we redirect), so this page mostly coordinates join/leave buttons,
 * shows the roster, and displays countdown to kickoff.
 *
 * Redirect params: `?paid=1` after Stripe success, `?cancelled=1` on
 * checkout cancel. We surface a toast + refresh.
 */
export function MatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [match, setMatch] = useState<Match | null>(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function load() {
      if (!id) return;
      const { data } = await supabase
        .from("matches")
        .select(`
          id, status, target_players, unit_price_minor, platform_fee_minor,
          currency, format,
          slot:availability_slots!matches_slot_id_fkey ( starts_at, ends_at ),
          court:courts (
            id, name,
            venue:venue_applications ( venue_name, city, address_line1 )
          ),
          participants:match_participants (
            id, user_id, status, joined_at,
            profile:profiles!match_participants_user_id_fkey ( display_name )
          )
        `)
        .eq("id", id)
        .single();
      if (!cancelled) setMatch((data ?? null) as Match | null);
    }

    load();

    // Poll every 10s while we're on the page so newly-joined players
    // show up without a manual refresh. Also catches the auto-confirm
    // moment when capacity is reached.
    const poll = setInterval(load, 10_000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [id]);

  // Surface Stripe redirect toasts on mount.
  useEffect(() => {
    const paid = params.get("paid");
    const cancelledParam = params.get("cancelled");
    if (paid) toast.success("Seat authorized - you're in.");
    if (cancelledParam) toast("Checkout cancelled.", { icon: "↻" });
    if (paid || cancelledParam) {
      params.delete("paid");
      params.delete("cancelled");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(
    () => (match?.participants ?? []).filter((p) => p.status === "authorized" || p.status === "captured"),
    [match]
  );
  const isParticipant = !!active.find((p) => p.user_id === user?.id);

  async function join() {
    if (!user) {
      navigate(`/login?next=/matches/${id}`);
      return;
    }
    setActing(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const jwt = sess?.session?.access_token;
      if (!jwt) throw new Error("Session expired");
      const res = await fetch(`${env.supabaseUrl}/functions/v1/match-join`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ match_id: id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error ?? `Server ${res.status}`);
      window.location.href = body.checkout_url as string;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't join");
      setActing(false);
    }
  }

  async function leave() {
    if (!window.confirm("Leave this match? If it hasn't confirmed, your hold is released. If it has, you'll be refunded.")) {
      return;
    }
    setActing(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const jwt = sess?.session?.access_token;
      if (!jwt) throw new Error("Session expired");
      const res = await fetch(`${env.supabaseUrl}/functions/v1/match-leave`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ match_id: id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error ?? `Server ${res.status}`);
      toast.success(body.status === "refunded" ? "Left. Refund on its way." : "Left.");
      // Refresh
      if (!id) return;
      const { data } = await supabase
        .from("matches")
        .select(`
          id, status, target_players, unit_price_minor, platform_fee_minor,
          currency, format,
          slot:availability_slots!matches_slot_id_fkey ( starts_at, ends_at ),
          court:courts (
            id, name,
            venue:venue_applications ( venue_name, city, address_line1 )
          ),
          participants:match_participants (
            id, user_id, status, joined_at,
            profile:profiles!match_participants_user_id_fkey ( display_name )
          )
        `)
        .eq("id", id)
        .single();
      setMatch((data ?? null) as Match | null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't leave");
    } finally {
      setActing(false);
    }
  }

  if (!match) return <PageLoader />;

  const charge = match.unit_price_minor + match.platform_fee_minor;
  const slotStart = match.slot ? new Date(match.slot.starts_at) : null;

  return (
    <section className="mx-auto max-w-3xl px-6 py-12">
      {match.court && (
        <Link
          to={`/courts/${match.court.id}`}
          className="mb-6 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Back to {match.court.name}
        </Link>
      )}

      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-volt-subtle px-2 py-0.5 text-[10px] font-bold text-volt">
            {match.format}
          </span>
          <StatusPill status={match.status} />
        </div>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight">
          {match.court?.name ?? "Match"}
        </h1>
        {match.court?.venue && (
          <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            {match.court.venue.venue_name}, {match.court.venue.address_line1},{" "}
            {match.court.venue.city}
          </p>
        )}
        {slotStart && (
          <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            {formatDateTime(slotStart)}
          </p>
        )}
      </header>

      {/* App download nudge - the join/leave flow only works for
          authenticated users; on web we show enough roster info to
          convince them to download. */}
      <DownloadAppCTA
        className="mb-8"
        headline="Join this run in the app"
        subhead="Download Whistll to lock in your seat. Stripe holds your card; you're only charged once the match fills, fully refunded otherwise."
      />

      {/* Roster + join/leave */}
      <section className="mb-8 rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wider">
            <Users className="mr-1 inline h-3.5 w-3.5" /> Roster ({active.length}/
            {match.target_players})
          </h2>
          <span className="text-xs font-semibold">
            <Wallet className="mr-1 inline h-3.5 w-3.5 text-volt" />
            {formatMoneyMinor(charge, match.currency)}/player
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full bg-volt transition-all"
            style={{
              width: `${Math.min(100, (active.length / match.target_players) * 100)}%`,
            }}
          />
        </div>

        {active.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nobody's joined yet. Be the first.
          </p>
        ) : (
          <ul className="space-y-2">
            {active.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between rounded-md bg-background px-3 py-2 text-sm"
              >
                <span>
                  {p.profile?.display_name ?? "Player"}
                  {p.user_id === user?.id && (
                    <span className="ml-2 rounded-full bg-volt-subtle px-2 py-0.5 text-[10px] font-bold text-volt">
                      You
                    </span>
                  )}
                </span>
                {p.status === "captured" && (
                  <CheckCircle2 className="h-3.5 w-3.5 text-volt" />
                )}
              </li>
            ))}
          </ul>
        )}

        {/* CTA */}
        <div className="mt-6 flex gap-3">
          {match.status === "pending" && !isParticipant && active.length < match.target_players && (
            <Button onClick={join} disabled={acting}>
              {acting ? "Opening Stripe…" : `Join - ${formatMoneyMinor(charge, match.currency)}`}
            </Button>
          )}
          {isParticipant && match.status !== "completed" && (
            <Button variant="outline" onClick={leave} disabled={acting}>
              <LogOut className="h-4 w-4" /> Leave match
            </Button>
          )}
        </div>
      </section>

      {/* Pricing breakdown */}
      <section className="rounded-2xl border border-dashed border-border bg-card/40 p-5 text-xs text-muted-foreground">
        <p>
          <span className="font-semibold text-foreground">
            {formatMoneyMinor(charge, match.currency)}
          </span>{" "}
          authorized per player when you join. Nothing's charged until the
          match confirms - if it doesn't fill up by tip-off,
          your hold is released. On confirm, the venue receives{" "}
          {formatMoneyMinor(match.unit_price_minor, match.currency)} and
          Whistll keeps {formatMoneyMinor(match.platform_fee_minor, match.currency)}{" "}
          as the service fee.
        </p>
      </section>
    </section>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    pending: {
      label: "Filling up",
      cls: "bg-[rgba(251,191,36,0.15)] text-[#fbbf24]",
    },
    confirmed: { label: "Confirmed", cls: "bg-volt/15 text-volt" },
    cancelled: {
      label: "Cancelled",
      cls: "bg-[rgba(248,113,113,0.15)] text-[#f87171]",
    },
    completed: { label: "Played", cls: "bg-secondary text-muted-foreground" },
  };
  const cfg = map[status] ?? map.pending;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function formatDateTime(d: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}
